import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface Kunde {
  id: number;
  uuid: string;
  niederlassung_code: string;
  suchbegriff: string;
  status_id: number;
  techniker_projekthinweis: string;
  startdatum: string;
  enddatum: string;
  anmeldung_besuche: string;
  periode_aufgaben_vorschlagen: number; // 0 = nein, 1 = ja
  ueberpruefung_erforderlich: string;
  bemerkung_next_evaluation: string;
  wiederkehrende_aufgaben_vorhanden: number; // 0 = nein, 1 = ja
  datum_letzte_wiederkehrende_aufgabe: string;
  techniker_id: number;
  
  // New address fields
  strasse?: string;
  plz?: string;
  ort?: string;
  land?: string;
  anmerkungen?: string;
  
  // Relationships (loaded via with())
  status?: KundeStatus;
  techniker?: Mitarbeiter;
  niederlassung?: Niederlassung;
}

export interface KundeStatus {
  id: number;
  code: string;
  beschreibung: string;
}

export interface Niederlassung {
  code: string;
  bezeichnung: string;
}

export interface Mitarbeiter {
  id: string; // UUID from database
  name: string;
  vorname: string;
  email: string;
}

export interface KundeResponse {
  result: number;
  data: Kunde[];
  total?: number;
  page?: number;
  limit?: number;
  sec: string;
}

export interface KundeSingleResponse {
  result: number;
  data: Kunde;
  sec: string;
}

@Injectable({
  providedIn: 'root'
})
export class KundeService {
  private apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) { 
    console.log('KundeService: Constructor called');
    console.log('KundeService: apiUrl set to:', this.apiUrl);
    console.log('KundeService: http client available:', !!this.http);
  }

  /**
   * Get all kunden with pagination
   */
  getKunden(page: number = 1, limit: number = 10, search: string = '', sortBy: string = 'suchbegriff', sortOrder: string = 'asc', statusId?: number): Observable<KundeResponse> {
    const params: any = {
      page: page.toString(),
      limit: limit.toString(),
      search: search,
      sortBy: sortBy,
      sortOrder: sortOrder
    };
    
    if (statusId !== undefined) {
      params.status_id = statusId.toString();
    }
    
    console.log('KundeService: Making API call to', `${this.apiUrl}/kunde`, 'with params:', params);
    return this.http.get<KundeResponse>(`${this.apiUrl}/kunde`, { params });
  }

  /**
   * Get active kunden optimized for dropdown use
   */
  getActiveKundenForDropdown(): Observable<KundeResponse> {
    const params = {
      page: '1',
      limit: '200', // Higher limit for dropdown
      search: '',
      sortBy: 'suchbegriff',
      sortOrder: 'asc',
      status_only: 'true' // Use status_only parameter for active clients
    };
    
    console.log('KundeService: Making dropdown API call to', `${this.apiUrl}/kunde`, 'with params:', params);
    return this.http.get<KundeResponse>(`${this.apiUrl}/kunde`, { params });
  }

  /**
   * Get single kunde by ID
   */
  getKunde(id: number): Observable<KundeSingleResponse> {
    return this.http.get<KundeSingleResponse>(`${this.apiUrl}/kunde/${id}`);
  }

  /**
   * Get single kunde by UUID
   */
  getKundeByUuid(uuid: string): Observable<KundeSingleResponse> {
    // Try using the UUID directly as the ID parameter
    return this.http.get<KundeSingleResponse>(`${this.apiUrl}/kunde/${uuid}`);
  }

  /**
   * Create new kunde
   */
  createKunde(kunde: Partial<Kunde>): Observable<KundeSingleResponse> {
    return this.http.post<KundeSingleResponse>(`${this.apiUrl}/kunde`, kunde);
  }

  /**
   * Update kunde
   */
  updateKunde(id: number | string, kunde: Partial<Kunde>): Observable<KundeSingleResponse> {
    return this.http.put<KundeSingleResponse>(`${this.apiUrl}/kunde/${id}`, kunde);
  }

  /**
   * Delete kunde
   */
  deleteKunde(id: number | string): Observable<KundeSingleResponse> {
    return this.http.delete<KundeSingleResponse>(`${this.apiUrl}/kunde/${id}`);
  }

  /**
   * Get all mitarbeiter for dropdown
   */
  getMitarbeiter(): Observable<any> {
    return this.http.get(`${this.apiUrl}/mitarbeiter`);
  }

  /**
   * Get all niederlassung for dropdown
   */
  getNiederlassungen(): Observable<any> {
    // This would need to be implemented in the backend
    // For now, return a mock response
    return new Observable(observer => {
      observer.next({
        result: 1,
        data: [
          { code: 'WIEN', bezeichnung: 'Wien' },
          { code: 'GRAZ', bezeichnung: 'Graz' },
          { code: 'LINZ', bezeichnung: 'Linz' },
          { code: 'SALZBURG', bezeichnung: 'Salzburg' },
          { code: 'INNSBRUCK', bezeichnung: 'Innsbruck' }
        ]
      });
      observer.complete();
    });
  }

  /**
   * Get all kunde status for dropdown
   */
  getKundeStatus(): Observable<any> {
    // This would need to be implemented in the backend
    // For now, return a mock response
    return new Observable(observer => {
      observer.next({
        result: 1,
        data: [
          { id: 1, code: 'FREIGEGEBEN', beschreibung: 'Freigabe' },
          { id: 2, code: 'NICHT_FREIGEGEBEN', beschreibung: 'Nicht freigegeben' },
          { id: 3, code: 'IN_BEARBEITUNG', beschreibung: 'In Bearbeitung' },
          { id: 4, code: 'ABGESCHLOSSEN', beschreibung: 'Abgeschlossen' }
        ]
      });
      observer.complete();
    });
  }

  /**
   * Get kontakte by kunde ID
   */
  getKontakteByKundeId(kundeId: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/kontakt?kunde_id=${kundeId}`);
  }

  /**
   * Get single kontakt by ID
   */
  getKontakt(kontaktId: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/kontakt/${kontaktId}`);
  }
}
